/* ── static data matching screenshot ── */

const volatilityData = [
  { name: 'High', market: 37, exposure: 14, color: '#E8B4B8' },
  { name: 'Medium', market: 19, exposure: 22, color: '#FCD34D' },
  { name: 'Low', market: 45, exposure: 86, color: '#93C5FD' },
];

const trendData = [
  { name: 'Trending', market: 55, exposure: 27, color: '#86EFAC' },
  { name: 'Transition', market: 21, exposure: 29, color: '#C4B5FD' },
  { name: 'Ranging', market: 25, exposure: 66, color: '#D1D5DB' },
];

const volatilitySelectivity = [
  { name: 'High', value: -23 },
  { name: 'Medium', value: 4 },
  { name: 'Low', value: 41 },
];

const trendSelectivity = [
  { name: 'Trending', value: -28 },
  { name: 'Transition', value: 9 },
  { name: 'Ranging', value: 41 },
];

/* ── component ── */

export default function RegimeCoverageMock() {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        borderRadius: 12,
        overflow: 'hidden',
        fontFamily:
          'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif',
      }}
    >
      {/* header */}
      <div style={{ background: '#111827', padding: '18px 24px 0' }}>
        <h3
          style={{
            color: '#f9fafb',
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          Market Regime Coverage
        </h3>
        <p style={{ color: '#9ca3af', fontSize: 12, margin: '6px 0 16px' }}>
          Market context and strategy interaction across volatility and trend
          conditions
        </p>
      </div>

      {/* 2x2 grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          padding: '12px 24px 24px',
          background: '#111827',
        }}
      >
        {/* top-left: Volatility Conditions */}
        <Card
          title="Volatility Conditions"
          subtitle="Market availability vs strategy exposure"
        >
          <RegimeBars data={volatilityData} />
          <Legend transitions={211} />
        </Card>

        {/* top-right: Trend Conditions */}
        <Card
          title="Trend Conditions"
          subtitle="Market availability vs strategy exposure"
        >
          <RegimeBars data={trendData} />
          <Legend transitions={220} />
        </Card>

        {/* bottom-left: Regime Selectivity */}
        <Card
          title="Regime Selectivity"
          subtitle="Strategy exposure relative to market baseline"
        >
          <div style={{ marginBottom: 12 }}>
            <SectionLabel>Volatility</SectionLabel>
            <SelectivityRow items={volatilitySelectivity} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <SectionLabel>Trend</SectionLabel>
            <SelectivityRow items={trendSelectivity} />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
              borderTop: '1px solid #374151',
              paddingTop: 10,
              fontSize: 11,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ color: '#9ca3af' }}>Dominant volatility</span>
              <span style={{ color: '#f9fafb', fontWeight: 700 }}>Low</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ color: '#9ca3af' }}>Dominant trend</span>
              <span style={{ color: '#f9fafb', fontWeight: 700 }}>
                Trending
              </span>
            </div>
          </div>
        </Card>

        {/* bottom-right: Observed Interaction */}
        <Card title="Observed Interaction" subtitle="">
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <div>
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#9ca3af',
                  margin: '0 0 6px',
                }}
              >
                Regime Coverage
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: '#9ca3af',
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                The test period was characterized by low volatility conditions.
                Observed behaviour reflects the volatility profile of the
                tested period.
              </p>
            </div>
            <div>
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#9ca3af',
                  margin: '0 0 6px',
                }}
              >
                Implications
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: '#9ca3af',
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                Strategy exposure shows selectivity relative to market
                availability. Performance outcomes may be sensitive to regime
                prevalence.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ── helper components ── */

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: '#1f2937',
        borderRadius: 8,
        padding: 14,
        border: '1px solid #374151',
      }}
    >
      <h4
        style={{
          color: '#f9fafb',
          fontSize: 13,
          fontWeight: 700,
          margin: '0 0 2px',
        }}
      >
        {title}
      </h4>
      {subtitle && (
        <p style={{ color: '#9ca3af', fontSize: 10, margin: '0 0 10px' }}>
          {subtitle}
        </p>
      )}
      {children}
    </div>
  );
}

function RegimeBars({
  data,
}: {
  data: { name: string; market: number; exposure: number; color: string }[];
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {data.map((d) => (
        <div key={d.name}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11,
              marginBottom: 4,
            }}
          >
            <span style={{ color: '#f9fafb', fontWeight: 600 }}>
              {d.name}
            </span>
            <span style={{ color: '#9ca3af' }}>
              {d.market}% &rarr; {d.exposure}%
            </span>
          </div>
          {/* stacked bar */}
          <div
            style={{
              position: 'relative',
              height: 12,
              background: '#374151',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            {/* market bar (wider, dimmer) */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${d.market}%`,
                background: d.color,
                opacity: 0.5,
                borderRadius: 3,
              }}
            />
            {/* exposure bar (brighter overlay) */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${d.exposure}%`,
                background: d.color,
                opacity: 0.9,
                borderRadius: 3,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Legend({ transitions }: { transitions: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 10,
        fontSize: 9,
        color: '#9ca3af',
      }}
    >
      <div style={{ display: 'flex', gap: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              width: 12,
              height: 8,
              background: '#9ca3af',
              opacity: 0.5,
              borderRadius: 2,
              display: 'inline-block',
            }}
          />
          Market
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              width: 12,
              height: 8,
              background: '#9ca3af',
              opacity: 0.9,
              borderRadius: 2,
              display: 'inline-block',
            }}
          />
          Exposure
        </span>
      </div>
      <span>{transitions} transitions</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#9ca3af',
        margin: '0 0 6px',
      }}
    >
      {children}
    </p>
  );
}

function SelectivityRow({
  items,
}: {
  items: { name: string; value: number }[];
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
        gap: 8,
      }}
    >
      {items.map((item) => {
        let color = '#d1d5db'; // neutral gray
        if (item.value > 5) color = '#60a5fa'; // blue-400
        if (item.value < -5) color = '#fb923c'; // orange-400
        return (
          <div key={item.name}>
            <p
              style={{
                fontSize: 11,
                color: '#9ca3af',
                margin: '0 0 2px',
              }}
            >
              {item.name}
            </p>
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color,
                margin: 0,
              }}
            >
              {item.value > 0 ? '+' : ''}
              {item.value}%
            </p>
          </div>
        );
      })}
    </div>
  );
}
